import styled from "styled-components";

import { DIGIPOSTHEME } from "theme/digiPosTheme";
import { BREAKPOINTS, MEDIA_QUERIES } from "context/media/MediaConstant";

interface FlexContentAligmentProps {
  justifyContent?: "center" | "flex-start" | "flex-end" | "space-between";
  alignItems?:
    | "center"
    | "flex-start"
    | "flex-end"
    | "space-between"
    | "initial";
}

interface FlexProps extends FlexContentAligmentProps {
  direction?: "row" | "column" | "row-reverse" | "column-reverse";
  flexWrap?: "wrap" | "wrap-reverse" | "no-wrap";
}

interface RowProps extends FlexProps {
  isResponsive?: boolean;
  class?: string;
  noMargin?: boolean;
  padding?: string;
  backgroundColor?: string;
  overflow?: "auto" | "hidden" | "unset";
  border?: string;
  borderBottom?: string;
  isCursor?: boolean;
  disabled?: boolean;
  colReverse?: boolean;
  gap?: boolean;
  width?: string;
}

export const Row = styled.div<RowProps>`
  cursor: ${({ isCursor }) => (isCursor ? "pointer" : "")};
  display: flex;
  justify-content: ${({ justifyContent = "space-between" }) => justifyContent};
  align-items: ${({ alignItems = "center" }) => alignItems};
  flex-direction: ${({ direction = "row" }) => direction};
  flex-wrap: ${({ flexWrap = "wrap" }) => flexWrap};

  margin: ${({ noMargin = false }) => (noMargin ? "0" : "0 20px 20px")};
  ${({ padding }) => padding && `padding:${padding}`};

  background-color: ${({ backgroundColor = DIGIPOSTHEME.colors.white }) =>
    backgroundColor};

  ${({ border }) =>
    border &&
    `border-width: ${border}; border-color: ${DIGIPOSTHEME.colors.secondaryGraySecondary}; border-style: solid`};

  ${({ width }) => width && `width: ${width};`};

  border-bottom: ${({ borderBottom }) => borderBottom};

  overflow: ${({ overflow = "unset" }) => overflow && overflow};

  pointer-events: ${({ disabled }) => (disabled ? "none" : null)};

  @media (max-width: ${BREAKPOINTS.MOBILE}px) {
    width: ${({ width = "100%" }) => width};

    ${({ isResponsive = false, colReverse = false }) =>
      isResponsive &&
      `flex-direction: ${colReverse ? "column-reverse" : "column"}`};

    margin: ${({ noMargin = false }) => (noMargin ? "0" : "0 0 15px")};

    ${({ colReverse = false }) => (colReverse ? "gap: 5px" : "")};
    ${({ gap = false }) => (gap ? "gap: 5px" : "")};
  }

  &::after {
    display: block;
    clear: both;
    content: "";
  }
`;

interface ColProps extends FlexProps {
  size?: number;
  offSet?: number;
  overflow?: string;
  minWidth?: string;
  maxWidth?: string;
  backgroundColor?: string;
  isDisplayFlex?: boolean;
  margin?: string;
}

export const Col = styled.div<ColProps>`
  flex: ${({ size = 1 }) => size};
  ${({ offSet }) => offSet && `margin-left: ${offSet}px`};
  justify-content: ${({ justifyContent = "flex-start" }) => justifyContent};
  align-items: ${({ alignItems = "flex-start" }) => alignItems};

  ${({ minWidth }) => minWidth && `min-width: ${minWidth}`};
  ${({ maxWidth }) => maxWidth && `max-width: ${maxWidth}`};

  overflow: ${({ overflow = "none" }) => overflow && overflow};

  ${({ backgroundColor }) =>
    backgroundColor && `background-color: ${backgroundColor};`};

  ${({ isDisplayFlex }) => isDisplayFlex && `display: flex`};

  ${({ margin }) => margin && `margin: ${margin}`};

  @media (max-width: ${BREAKPOINTS.MOBILE}px) {
    width: 100%;
    max-width: none;
    margin: 0;
  }
`;

interface ColTitleProps {
  margin?: string;
  color?: string;
}

export const ColTitle = styled.div<ColTitleProps>`
  width: fit-content;

  display: flex;
  align-items: center;

  margin: ${({ margin = "0" }) => margin};
  padding: 0 0 8px;

  color: ${({ color = "black" }) => color};
  font-family: "Campton";
  font-size: 18px;
  font-weight: 500;

  @media (max-width: ${BREAKPOINTS.MOBILE}px) {
    padding: 0;
    margin-bottom: 20px;
  }
`;

interface SeperatorProps {
  marginTop?: number;
  marginRight?: number;
}

export const Seperator = styled.div<SeperatorProps>`
  padding-top: ${({ marginTop = 10 }) => `${marginTop}px`};
  padding-right: ${({ marginRight = 0 }) => `${marginRight}px`};
`;

interface ElementWrapperProps {
  display?: string;
  width?: string;
  height?: string | number;
  direction?: "row" | "column" | "row-reverse" | "column-reverse";
  justifyContent?: "center" | "flex-start" | "flex-end" | "space-between";
  alignItems?: "center" | "flex-start" | "flex-end" | "space-between";
  margin?: string;
  padding?: string;
  mobileWidth?: string;
  minHeight?: string;
  backgroundColor?: string;
  overflow?: "auto" | "hidden";
  borderRadius?: string;
  position?: "absolute" | "relative";
  right?: string;
  left?: string;
  bottom?: string;
  top?: string;
}

export const ElementWrapper = styled.div<ElementWrapperProps>`
  ${({ position }) => position && `position: ${position}`};
  ${({ right }) => right && `right: ${right}`};
  ${({ left }) => left && `left: ${left}`};
  ${({ bottom }) => bottom && `bottom: ${bottom}`};
  ${({ top }) => top && `top: ${top}`};

  width: ${({ width = "auto" }) => width};
  ${({ height = "auto" }) => height && `height: ${height}`};

  ${({ minHeight }) => minHeight && `min-height: ${minHeight}`};

  display: ${({ display = "flex" }) => display};
  flex-direction: ${({ direction = "row" }) => direction};
  justify-content: ${({ justifyContent = "center" }) => justifyContent};
  align-items: ${({ alignItems }) => alignItems};
  ${({ overflow }) => overflow && `overflow:${overflow};`};

  ${({ backgroundColor }) =>
    backgroundColor && `background-color:${backgroundColor};`};

  margin: ${({ margin = "auto" }) => margin};
  ${({ padding }) => padding && `padding:${padding}`};

  ${({ borderRadius }) => borderRadius && `border-radius:${borderRadius}`};

  @media (max-width: ${BREAKPOINTS.MOBILE}px) {
    width: ${({ mobileWidth }) => mobileWidth || "100%"};
    padding: 0;
    display: flex;
    flex-direction: column;
  }
`;

interface StyledLabelProps {
  width?: string;
  minWidth?: string;
  inputSize?: "small" | "medium";
  fontSize?: string;
  top?: string;
  isWhite?: boolean;
}

export const StyledLabel = styled.label<StyledLabelProps>`
  height: 33px;

  display: flex;
  position: absolute;

  left: 10px;
  top: 6px;
  align-items: center;

  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.secondaryGrayPrimary};
  font-family: "Campton";
  font-size: 14px;
  font-weight: 500;
  pointer-events: none;

  @media ${MEDIA_QUERIES.MOBILE} {
    font-size: 12px;
    height: 28px;
  }
`;

export const StyledErrorLabel = styled.label<StyledLabelProps>`
  width: ${({ inputSize }) => {
    if (inputSize === "small") return "71%";

    return "100%";
  }};
  height: 8px;

  top: ${({ top = "6px" }) => top};

  position: relative;
  display: flex;
  align-items: center;

  padding-left: 12px;

  color: ${({ isWhite }) =>
    isWhite
      ? DIGIPOSTHEME.colors.white
      : DIGIPOSTHEME.colors.secondaryGrayPrimary}
  font-family: "Campton";
  font-size: ${({ fontSize = "10px" }) => fontSize};
  line-height: 1.2;
  letter-spacing: 0.23px;

  @media ${MEDIA_QUERIES.MOBILE} {
    top: ${({ top = "3px" }) => top};

    line-height: 1.1;

    width: ${({ inputSize }) => {
      if (inputSize === "small") return "28%";

      return "100%";
    }};
  }
`;

export const StyledFormWrapper = styled.div<StyledLabelProps>`
  position: relative;
  margin-bottom: 32px;
  padding-bottom: 5px;

  background-color: ${DIGIPOSTHEME.colors.white};

  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;

  @media ${MEDIA_QUERIES.MOBILE} {
    padding: 0 5px 5px;
  }
`;
// @media ${MEDIA_QUERIES.MOBILE} { bu yukatidaki 287 ci satirda  10px 5px olarak degisti
//   padding: 0 20px 5px;
// }
export const CheckBoxGroupWrapper = styled.div`
  margin-left: 35px;
  & > :first-child > :nth-child(3) {
    margin-right: 50px;
  }

  & > :first-child > :nth-child(6) {
    margin-right: 86px;
  }

  & > :first-child > * {
    margin-top: 0;
  }

  @media ${MEDIA_QUERIES.MOBILE} {
    margin-left: 5px;
    & > :first-child > :nth-child(3) {
      margin-right: 16px;
    }

    & > :first-child > :nth-child(6) {
      margin-right: 58px;
    }

    & > :first-child > * {
      margin-top: 0;
    }
  }
`;
interface StyledOverFlowProps {
  maxHeight: string;
}
export const StyledOverFlow = styled.div<StyledOverFlowProps>`
  overflow-y: auto;
  max-height: ${({ maxHeight }) => maxHeight};
`;
